import React, { useEffect } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { useLocalStorage } from 'usehooks-ts';
import { useGetUserSettings } from '@juno/client-api';
import { messengerTabMap } from '@juno/modules';
import { DialogAriaWrapper } from '@juno/ui';
import {
  CallUnansweredStatus,
  useMessengerContext,
  useSessionContext,
  useSettings,
} from '@juno/utils';
import { CallStatusLocalStorageValues } from '../SessionPage/constants';

export const CallDeclinedDialog: React.FC = () => {
  const { user, setShowMessenger, featureConfigs, siteId } = useSettings();
  const { enableMessenger = false } = featureConfigs?.user_and_profile || {};
  const { setCallState, callState } = useSessionContext();
  const { dialogStatus: status, dialogOpen: open, isCaller, otherUser } = callState;
  const { startConversation, setMessengerTab, setIsConversation, setCurrentChannel } =
    useMessengerContext();
  const statusText = status === CallUnansweredStatus.DECLINED ? 'declined' : 'not answered';
  const [callStatus, setCallStatus] = useLocalStorage(CallStatusLocalStorageValues.callStatus, {
    value: '',
  });
  const [callClosedValue, setcallClosedValue] = useLocalStorage(
    CallStatusLocalStorageValues.declinedCall,
    { value: false },
  );
  const [otherUserId, setOtherUserId] = useLocalStorage(CallStatusLocalStorageValues.otherUserId, {
    value: '',
  });
  const [otherUserFirstName, setOtherUserFirstName] = useLocalStorage(
    CallStatusLocalStorageValues.otherUserFirstName,
    { value: '' },
  );
  const [otheruserLastName, setOtherUserLastName] = useLocalStorage(
    CallStatusLocalStorageValues.otherUserLastName,
    { value: '' },
  );

  const cleanCallStatusStorage = () => {
    localStorage.removeItem(CallStatusLocalStorageValues.callStatus);
    localStorage.removeItem(CallStatusLocalStorageValues.declinedCall);
    localStorage.removeItem(CallStatusLocalStorageValues.otherUserId);
    localStorage.removeItem(CallStatusLocalStorageValues.otherUserFirstName);
    localStorage.removeItem(CallStatusLocalStorageValues.otherUserLastName);
  };

  const handleClose = () => {
    setCallState({});
    cleanCallStatusStorage();
  };
  const startChat = (userId: string) => {
    if (!user) return;
    setShowMessenger && setShowMessenger(true);
    startConversation([userId, user?.id]).then((channelId) => {
      if (channelId) {
        setMessengerTab(messengerTabMap.Messenger);
        setCurrentChannel(channelId);
        setIsConversation(true);
      }
    });
    setCallState({});
  };

  useEffect(() => {
    if (status) {
      if (callState.isCaller) {
        setcallClosedValue({ value: true });
        setOtherUserId({ value: otherUser?.id || '' });
        setOtherUserFirstName({ value: otherUser?.first_name || '' });
        setOtherUserLastName({ value: otherUser?.last_name || '' });
        setCallStatus({ value: status });
      }
    }
  }, [status]);
  const { data: callerUserSettings } = useGetUserSettings(siteId, {
    filter: { user__id__in: [otherUser?.id, user?.id] },
  });

  const isPrivateMessagingDisabled = callerUserSettings?.some(
    (setting) => setting.disabled_private_messaging === true,
  );

  return (
    <DialogAriaWrapper open={!!open} onClose={handleClose} id='message_other_user_instead'>
      <DialogTitle
        sx={{
          width: {
            sm: 600,
          },
          textAlign: 'center',
          justifyContent: 'center',
          display: 'flex',
          mx: 'auto',
        }}
      >
        Private Call with {otherUser?.first_name} {otherUser?.last_name}
      </DialogTitle>
      <Divider sx={{ mx: 2 }} />
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          textAlign: 'center',
          width: {
            sm: 600,
          },
          minHeight: 150,
          mx: 'auto',
        }}
      >
        <Typography variant='subtitle1' sx={{ mb: 1 }}>
          {isCaller ? `Your call was ${statusText}.` : 'You declined the call.'}
        </Typography>
        {enableMessenger && !isPrivateMessagingDisabled && (
          <Typography variant='body2' sx={{ mb: 1 }}>
            Would you like to send them a message instead?
          </Typography>
        )}
      </DialogContent>
      <Divider sx={{ mx: 2 }} />
      <DialogActions sx={{ p: 2 }}>
        {enableMessenger && !isPrivateMessagingDisabled ? (
          <>
            <Button
              variant='contained'
              fullWidth
              onClick={() => startChat(otherUser?.id || '')}
              aria-label={'Send message'}
            >
              Yes
            </Button>
            <Button
              variant='outlined'
              fullWidth
              onClick={handleClose}
              aria-label={"Don't send message"}
            >
              No
            </Button>
          </>
        ) : (
          <>
            <Button variant='contained' fullWidth onClick={handleClose} aria-label={'Dismiss'}>
              Dismiss
            </Button>
          </>
        )}
      </DialogActions>
    </DialogAriaWrapper>
  );
};
